import { Box, FormControlLabel, styled } from '@mui/material';
import { StyledCustomTextField } from 'components/shared/PlanUnitSelector/PlanUnitSelector.styles';

/** The two view-setting checkboxes, laid out side by side rather than stacked. */
export const BacklogCheckboxRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: theme.spacing(2)
}));

/**
 * One view-setting checkbox with its label. The theme sets a spacing(2) marginLeft on every form-control
 * label, meant for spaced-out forms; here it leaves too much air between the box and its word, so it is
 * pulled back to a tight 2px gap.
 */
export const ViewSettingCheckboxLabel = styled(FormControlLabel)(
  ({ theme }) => ({
    '& .MuiFormControlLabel-label': {
      marginLeft: theme.spacing(0.25)
    }
  })
);

// The takenlijst and the reused appointment-edit drawer sit side by side in a full-height row, so the
// drawer opens on the right and pushes the table rather than stacking under it. Mirrors afsprakenplanning's
// MultiResourceContainer (a flex row holding the content and the AppointmentForm drawer).
export const BacklogDrawerRow = styled(Box)({
  display: 'flex',
  height: '100%',
  width: '100%',
  overflow: 'hidden'
});

export const BacklogPageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullWidth'
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  // Allow the column to shrink below its content width inside the row, then leave room for the 660px
  // drawer when it is open — the same way afsprakenplanning's CalendarSection makes way for the form.
  minWidth: 0,
  maxWidth: fullWidth ? '100%' : 'calc(100% - 660px)',
  height: '100%',
  overflow: 'hidden'
}));

export const BacklogTilesArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 4, 0, 4),
  minWidth: 0
}));

// Bounds the grid rather than scrolling itself: the grid handles its own scrolling so the week
// headers stay put. minHeight 0 lets it shrink inside the flex column instead of pushing out.
export const BacklogContentArea = styled(Box)({
  // Fills the toolbar's content slot and bounds the grid, which scrolls itself so the week headers
  // stay pinned. The outer padding now lives on BacklogToolbarArea.
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minWidth: 0,
  minHeight: 0,
  height: '100%'
});

export const BacklogStateArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(8)
}));

// Holds the shared ToolbarWrapper. It bounds the toolbar plus the grid, so the grid still gets a
// definite height and keeps its week headers pinned.
// A uniform spacing(4) gutter, so the toolbar card is not flush against the tiles above it. That is
// the same 16px ToolbarWrapper itself puts between the toolbar and its content (its container's
// gap), which leaves the card sitting in even space rather than in a gap chosen by eye.
export const BacklogToolbarArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  minWidth: 0,
  minHeight: 0,
  padding: theme.spacing(4)
}));

/**
 * Stacks a slot's controls when they are shown in the docked toolbar's popper instead of the top row.
 *
 * Just enough gap to keep the second field's floating label off the first field's border — outlined
 * inputs draw their label on the border itself, so flush fields read as cramped.
 */
export const BacklogControlStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  minWidth: 0
}));

/**
 * One field in the view-settings popover: 200px wide, shrinking to a still-readable 160 before it wraps.
 * The filters used to share this and wanted more room for long care receiver names; they now lay
 * themselves out in the filter popper's own grid, so this is back to the width its one remaining field —
 * the cell-value mode select — actually needs.
 *
 * A width rather than `flex: '0 1 200px'`, deliberately. flex-basis sizes the MAIN axis, so that
 * shorthand meant 200px wide in a horizontal toolbar but 200px *tall* in the docked popper's column,
 * leaving each 40px field sitting in a 200px box. A width is the same thing on a row and the right axis
 * on a column; grow 0 and shrink 1 are the defaults the shorthand was spelling out anyway.
 */
export const BacklogFieldContainer = styled(Box)({
  width: 200,
  minWidth: 160
});

/**
 * The toolbar dropdowns, all three of them.
 *
 * isToolbar sets marginTop: 0 on the input, because the plan unit selector pairs it with hiddenLabel and
 * so needs no room above. These fields do show their label, so that room has to come back — 16px is what
 * a standard variant reserves by default — or the label sits on top of the value.
 *
 * The label is kept shrunk whatever the state. Left to itself it only floats up once the field is filled,
 * which made the two filters read at full size on the line while Weergave, always having a value, read
 * small and above: the same control looking like two different ones.
 */
export const BacklogToolbarField = styled(StyledCustomTextField)(
  ({ theme }) => ({
    '& .MuiInputBase-root.MuiInput-root.MuiInput-underline': {
      // The plan unit selector's isToolbar pairs with hiddenLabel and so drops the space above the
      // input; these fields show their label, so the 16px a standard variant reserves is put back.
      marginTop: theme.spacing(2)
    }
  })
);

/**
 * Wraps one filter field to hold it to a single line — and that is all it does, so the fields stay the plain
 * outlined fields the flex match filter popper uses.
 *
 * `flexWrap: nowrap` is the whole trick: MUI lets the selection area wrap, so a picked value would push the
 * input onto a second row and knock the popover's two-per-row grid out of alignment. The summary does its own
 * clipping (see {@link BacklogFilterTagSummary}) and minWidth: 0 lets it and the input shrink to allow it — a
 * flex item defaults to its content width, which would otherwise win over the ellipsis.
 *
 * Never put `overflow: hidden` on the input root. MUI draws an outlined field's box with a `<fieldset>`
 * positioned at `top: -5px`, deliberately sticking out above the input so the label can sit in its top
 * border. Clipping the input root therefore cuts off that top border and the top of both sides, leaving the
 * field looking like a bare underline with a stub at the right — which is exactly what happened when the
 * one-line rule above was first written with overflow on it.
 */
export const BacklogFilterFieldBox = styled(Box)({
  '& .MuiAutocomplete-inputRoot': {
    flexWrap: 'nowrap',
    '& .MuiAutocomplete-input': {
      minWidth: 0
    }
  }
});

/**
 * The selection shown on a filter field — a named pick or a count. Ellipsises rather than wrapping, so a
 * long "{number} - {surname}, {given names}" cannot grow the field.
 */
export const BacklogFilterTagSummary = styled('span')({
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
});

/**
 * The popover body: the activity field, the heading of the entity on screen, and that entity's fields.
 *
 * Capped in height with its own scroll, because a section's fields plus a long selection can outgrow the
 * screen — the footer with Filters wissen and Toepassen has to stay reachable whatever is open.
 *
 * It sits as close under the title as it can. That is only safe because the topmost field is a standard
 * one, whose shrunk label stays inside the field's own box: an outlined field paints its filled label 9px
 * above that box, and this being the scroll container, anything above the content edge is clipped — the
 * sliced-off label the activity field used to show the moment something was picked. So if an outlined field
 * ever becomes the first row here again, it needs that room back.
 */
export const BacklogFilterSectionStack = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  maxHeight: '60vh',
  overflowY: 'auto',
  // Never sideways. A scroll container cannot mix scrolling with visible overflow, so `overflow-y: auto`
  // silently turns the horizontal axis into `auto` as well — and then a single stray pixel out of a field's
  // flex row hands the planner a scrollbar across a popover of a fixed width, where there is nothing to
  // scroll to: every field in here summarises its selection and ellipsises what does not fit.
  overflowX: 'hidden',
  // Pulled back over the top margin the popover's content wrapper carries, rather than editing the shared
  // popper: its other pages open on a plain first row and need that margin. A standard field reserves the
  // space for its own label above the input, which is the breathing room under the title.
  marginTop: theme.spacing(-1)
}));
